/**
 * Copy glossary (user-facing prose):
 *   Email page = the whole screen · Mailbox = whose inbox · message list = the table of rows
 *   (also the Go to / Back destination) · this page = the current page of rows only ·
 *   window = the separate-window open mode (side pane / full view / window all host EmailMessage) ·
 *   Move to trash / Don’t trash = what happens to the
 *   Gmail message after connect. Never bare “list” or “mail list” in UI copy;
 *   in code comments, “list” is fine shorthand for the message list.
 */
export const ALL_PEOPLE_MAILBOX_ID = 'all-non-spam'; // 1 Jul 2026 JCH: synthetic aggregate mailbox
export const ALL_PEOPLE_MAILBOX_NAME = 'All mailboxes'; // 3 Jul 2026 JCH: legacy id all-non-spam; label is accurate for merged view

export const CONNECT_FIRM_REQUIRED_MSG = 'Select firm to connect.';

/** Connect popup (EmailMessage) — opened from message list row / single-select connect. */
export const EMAIL_MESSAGE_WINDOW_WIDTH = 960;
const EMAIL_MESSAGE_WINDOW_HEIGHT = 820;

/** Cap iframe height for stored note HTML (Edit Note, expansion) — well above marketing emails. */
export const EMAIL_IFRAME_MAX_HEIGHT = 50000;

/** Plain connect note body — legacy limit. */
export const NOTE_BODY_PLAIN_MAX = 32000;

/** HTML connect note body — marketing emails exceed plain limit (styles, MSO blocks).
 * 10 Jul 2026 JCH: NotesAPIService sends big notes as multipart (nu-api JSON cap 100kb);
 * multipart field ceiling is 1MB, so 512k chars keeps ~2x headroom. */
export const NOTE_BODY_HTML_MAX = 512000;

export function emailMessageWindowFeatures(left = 500, top = 22) {
  return `resizeable=yes, width=${EMAIL_MESSAGE_WINDOW_WIDTH}, left=${left}, top=${top}, height=${EMAIL_MESSAGE_WINDOW_HEIGHT}`;
}

/** How emails open from the message list — side pane (default), Gmail-style full view, or the popup window. */
export const EMAIL_OPEN_MODE_STORAGE_KEY = 'nu-email-open-mode';
export const EMAIL_OPEN_WINDOW = 'window';
export const EMAIL_OPEN_FULL = 'full';
export const EMAIL_OPEN_PANE = 'pane';
export const EMAIL_OPEN_MODE_DEFAULT = EMAIL_OPEN_PANE;

const OPEN_MODES = new Set([EMAIL_OPEN_WINDOW, EMAIL_OPEN_FULL, EMAIL_OPEN_PANE]);

export function readEmailOpenModePref() {
  if (typeof window === 'undefined' || !window.localStorage) return EMAIL_OPEN_MODE_DEFAULT;
  const raw = window.localStorage.getItem(EMAIL_OPEN_MODE_STORAGE_KEY);
  return OPEN_MODES.has(raw) ? raw : EMAIL_OPEN_MODE_DEFAULT;
}

export function writeEmailOpenModePref(mode) {
  if (typeof window === 'undefined' || !window.localStorage) return;
  const next = OPEN_MODES.has(mode) ? mode : EMAIL_OPEN_MODE_DEFAULT;
  window.localStorage.setItem(EMAIL_OPEN_MODE_STORAGE_KEY, next);
}

/** Side-pane width in px, set by dragging the divider. null = CSS default (clamp). */
export const EMAIL_PANE_WIDTH_STORAGE_KEY = 'nu-email-pane-width';
export const EMAIL_PANE_MIN_WIDTH = 360;
// 13 Jul 2026 JCH: rows h-scroll now, so the list can go much narrower — floor keeps
// the divider grabbable and checkbox + sender start visible.
export const EMAIL_LIST_MIN_WIDTH = 280;

export function readEmailPaneWidthPref() {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  const raw = window.localStorage.getItem(EMAIL_PANE_WIDTH_STORAGE_KEY);
  const n = Number(raw);
  if (!raw || !Number.isFinite(n) || n < EMAIL_PANE_MIN_WIDTH) return null;
  return Math.round(n);
}

export function writeEmailPaneWidthPref(px) {
  if (typeof window === 'undefined' || !window.localStorage) return;
  const n = Number(px);
  if (!Number.isFinite(n) || n < EMAIL_PANE_MIN_WIDTH) {
    window.localStorage.removeItem(EMAIL_PANE_WIDTH_STORAGE_KEY);
    return;
  }
  window.localStorage.setItem(EMAIL_PANE_WIDTH_STORAGE_KEY, String(Math.round(n)));
}

export const EMAIL_FUTURE = { // 6 May 2026 JCH: flip true when API adds each feature
  connectedBucket: false,
  trashBucket: false,
  serverSearch: false,
};

export function needsEmailApi(feature) { // 6 May 2026 JCH: UI treats flag as not backed yet
  return !EMAIL_FUTURE[feature];
}

/** Trash-after-connect checkbox — strongly recommended until Connected bucket exists. */
export const TRASH_AFTER_CONNECT_LABEL = needsEmailApi('connectedBucket')
  ? 'Move email to trash after successful connect (strongly recommended)'
  : 'Move email to trash after successful connect (default)';

function mailboxLocalKeys(m) { // 6 May 2026 JCH: login id/local-part only, not full email
  const id = String((m && m.id) || '').toLowerCase();
  const addr = String((m && (m.address || m.email)) || '').toLowerCase();
  const fromId = id.includes('@') ? id.split('@')[0] : id;
  const fromAddr = addr.includes('@') ? addr.split('@')[0] : '';
  return [...new Set([fromId, fromAddr].filter(Boolean))];
}

/**
 * Short login (no dot): exact on id/local-part only — larry → larry, not larry.hilton.
 * Dotted login: exact, then dotted-prefix family; longest id wins in the caller.
 */
export function mailboxMatchesUsername(m, username) {
  const u = String(username || '').trim().toLowerCase();
  if (!u) return false;
  const keys = mailboxLocalKeys(m);
  if (keys.some((key) => key === u)) return true;
  if (!u.includes('.')) return false;
  return keys.some((key) => key.startsWith(`${u}.`) || u.startsWith(`${key}.`));
}

function personMailboxes(mailboxes) {
  return (Array.isArray(mailboxes) ? mailboxes : [])
    .filter((m) => m && m.id && m.id !== ALL_PEOPLE_MAILBOX_ID);
}

export function pickUsernameMailboxId(mailboxes, username) { // 6 May 2026 JCH: default from username, never first row
  const u = String(username || '').trim();
  if (!u) return null;
  const matches = personMailboxes(mailboxes).filter((m) => mailboxMatchesUsername(m, u));
  if (!matches.length) return null;
  if (matches.length === 1) return matches[0].id;
  return matches.sort((a, b) => String(b.id).length - String(a.id).length)[0].id;
}

/** 13 Jul 2026 JCH: Name (email) — names collide; address is the real mailbox id. */
function mailboxWarningLabel(m) {
  const name = ((m && m.name) || '').trim();
  const email = String((m && (m.address || m.email || m.id)) || '').trim();
  if (name && email && name.toLowerCase() !== email.toLowerCase()) {
    return `${name} (${email})`;
  }
  return name || email || '';
}

/**
 * 10 Jul 2026 JCH: friction, not blocking — trash confirm warns when selected
 * rows sit in someone else's mailbox (trash also removes them from that inbox).
 * Returns '' when every row is the signed-in user's own mail.
 */
export function otherMailboxTrashWarning(rows, mailboxes, username, fallbackId) {
  const list = Array.isArray(rows) ? rows : [];
  const byId = new Map((Array.isArray(mailboxes) ? mailboxes : []).map((m) => [m && m.id, m]));
  let count = 0;
  const labels = [];
  const seen = new Set();
  list.forEach((r) => {
    const id = (r && r.mailbox_id) || fallbackId;
    if (!id) return;
    const m = byId.get(id) || { id };
    if (mailboxMatchesUsername(m, username)) return;
    count += 1;
    if (!seen.has(id)) {
      seen.add(id);
      labels.push(mailboxWarningLabel(m));
    }
  });
  if (!count) return '';
  const what = count === list.length
    ? (count === 1 ? 'This email is' : 'These emails are')
    : `${count} of these emails are`;
  const where = labels.length === 1 ? `${labels[0]}'s mailbox` : `other mailboxes (${labels.join(', ')})`;
  const them = count === 1 ? 'it' : 'them';
  const inbox = labels.length === 1 ? 'that inbox' : 'those inboxes';
  return `${what} in ${where} — Trash removes ${them} from ${inbox} too.`;
}
